import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-6 text-left overflow-hidden h-full max-h-[300px]"
}
const _hoisted_2 = { class: "flex flex-col gap-3 overflow-auto" }
const _hoisted_3 = { class: "text-gray-600 font-semibold flex items-center justify-between gap-2 truncate" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["disabled", "checked"]
const _hoisted_6 = ["disabled"]
const _hoisted_7 = {
  key: 1,
  class: "flex items-center justify-center h-[200px]"
}

import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/loading_state/LoadingSpinner.vue";
import ModalTW from "@/components/modals/ModalTW.vue";
import { useCreateOrUpdateDashboard } from "@/composables/dashboard";
import { Dashboard, DashboardWidgetToAddOrRemove, Widget } from "@/types/Dashboard";
import {
  useDashboardsWithDefault,
  useHasPermissionToEditDashboard,
  useLayoutContext,
} from "@/views/dashboard/composables";
import {
  addWidgetToDashboard,
  createDashboardMenuCategories,
} from "@/views/dashboard/services/projectOverviewV2Layout";


export default /*#__PURE__*/_defineComponent({
  __name: 'AddToDashboardsModal',
  props: {
    type: {},
    arg: {}
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();
const { layoutContext, isLoading: isLayoutContextLoading } = useLayoutContext();
const { dashboardsWithDefault, isLoading: areDashboardsLoading } =
  useDashboardsWithDefault(layoutContext);
const selectedDashboards = ref<Dashboard[]>([]);
const { createOrUpdateDashboard, isLoading: isDashboardCreatingOrUpdating } =
  useCreateOrUpdateDashboard();
const hasPermissionToEditDashboard = useHasPermissionToEditDashboard();

const isLoading = computed(() => areDashboardsLoading.value || isLayoutContextLoading.value);

const categories = computed(() =>
  createDashboardMenuCategories(dashboardsWithDefault.value).filter(
    (category) => category.dashboards.length > 0 && hasPermissionToEditDashboard(category),
  ),
);

const update = async (dashboard: Dashboard) => {
  if (!layoutContext.value) {
    return;
  }
  const widget: DashboardWidgetToAddOrRemove = { type: props.type, arg: props.arg };
  const updatedDashboard = addWidgetToDashboard(dashboard, widget, layoutContext.value);
  if (updatedDashboard) {
    await createOrUpdateDashboard(updatedDashboard);
  }
};

const toggleDashboard = (dashboard: Dashboard) => {
  if (isDashboardCreatingOrUpdating.value) {
    return;
  }
  if (selectedDashboards.value.includes(dashboard)) {
    const index = selectedDashboards.value.indexOf(dashboard);
    if (index > -1) {
      selectedDashboards.value.splice(index, 1);
    }
  } else {
    selectedDashboards.value.push(dashboard);
  }
};

const handleSaveClick = () => {
  if (isDashboardCreatingOrUpdating.value) {
    return;
  }
  Promise.all(selectedDashboards.value.map((dashboard) => update(dashboard)))
    .then(() => {
      emit("close");
    })
    .catch(() => undefined);
};

const handleClose = () => {
  emit("close");
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalTW, {
    open: true,
    onClose: handleClose,
    customCls: "w-full lg:w-[400px] m-2"
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("dashboard.add_to_dashboard")), 1)
    ]),
    content: _withCtx(() => [
      (!isLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (category) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: category.type
                }, [
                  _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)(`dashboard.menu_categories.${category.type}`)), 1),
                  _createElementVNode("div", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(category.dashboards, (dashboard) => {
                      return (_openBlock(), _createElementBlock("div", {
                        class: _normalizeClass(["py-2 px-4 rounded-md flex items-center cursor-pointer", _unref(isDashboardCreatingOrUpdating) ? '' : 'hover:bg-yellow-200']),
                        key: dashboard._id,
                        onClick: ($event: any) => (toggleDashboard(dashboard))
                      }, [
                        _createElementVNode("input", {
                          type: "checkbox",
                          disabled: _unref(isDashboardCreatingOrUpdating),
                          checked: selectedDashboards.value.includes(dashboard),
                          class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer disabled:text-gray-300"
                        }, null, 8, _hoisted_5),
                        _createElementVNode("label", {
                          class: _normalizeClass(["ml-3 text-sm truncate cursor-pointer", { 'text-gray-400': _unref(isDashboardCreatingOrUpdating) }])
                        }, _toDisplayString(dashboard.name), 3)
                      ], 10, _hoisted_4))
                    }), 128))
                  ])
                ]))
              }), 128))
            ]),
            _createElementVNode("button", {
              type: "submit",
              onClick: handleSaveClick,
              disabled: _unref(isDashboardCreatingOrUpdating),
              class: "shrink-0 focus:outline-none inline-flex gap-2 w-full items-center justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 font-medium text-white shadow-sm hover:bg-yellow-700 disabled:bg-gray-200"
            }, [
              (_unref(isDashboardCreatingOrUpdating))
                ? (_openBlock(), _createBlock(LoadingSpinner, {
                    key: 0,
                    size: "w-5 h-5"
                  }))
                : _createCommentVNode("", true),
              _createTextVNode(" " + _toDisplayString(_unref(t)("dashboard.add_button")), 1)
            ], 8, _hoisted_6)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createVNode(LoadingSpinner)
          ]))
    ]),
    _: 1
  }))
}
}

})